import Vue from 'vue'
import cookies from 'js-cookie'
import linkify from 'vue-linkify'
import { datadogRum } from '@datadog/browser-rum'

import dayjs from 'dayjs'
import { ModuleRegistry } from '@ag-grid-community/core'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { AdvancedFilterModule } from '@ag-grid-enterprise/advanced-filter'
import { ClipboardModule } from '@ag-grid-enterprise/clipboard'
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { MasterDetailModule } from '@ag-grid-enterprise/master-detail'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { MultiFilterModule } from '@ag-grid-enterprise/multi-filter'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { SetFilterModule } from '@ag-grid-enterprise/set-filter'
import { RichSelectModule } from '@ag-grid-enterprise/rich-select'

// Register all modules
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  CsvExportModule,
  AdvancedFilterModule,
  ClipboardModule,
  ColumnsToolPanelModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  MasterDetailModule,
  MenuModule,
  MultiFilterModule,
  RangeSelectionModule,
  RowGroupingModule,
  ServerSideRowModelModule,
  RichSelectModule,
  SetFilterModule
])

import utc from 'dayjs/plugin/utc'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone.js'

dayjs.extend(utc)
dayjs.extend(relativeTime)
dayjs.extend(timezone)
Vue.directive('linkified', linkify)

const isProd = import.meta.env.VITE_ENV == 'production'
const isLocal = import.meta.env.VITE_ENV == 'local'

// datadog logging
// If we're not in local development, initialize datadog
if (!isLocal) {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DATADOG_APP_ID,
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'newsdesk-ui',
    env: import.meta.env.VITE_ENV || 'production',
    version: isProd ? import.meta.env.VITE_VERSION : import.meta.env.VITE_GIT_COMMIT_HASH,
    // allowedTracingUrls: [(url) => url.startsWith('https://newsdesk-api.scrippsnet.')],
    sessionSampleRate: 100,
    sessionReplaySampleRate: isProd ? 20 : 0,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: (event, context) => {
      // discard 404 network errors
      if (event.http?.status_code === 404) return false
      if (event.http?.status_code === 401) return false

      // discare any errors that contain these messages
      let ignoredMessages = ['ResizeObserver loop limit exceeded', 'timeout', 'Network Error']
      if (event.type === 'error' && ignoredMessages.some((message) => event.error.message.includes(message))) {
        return false
      }
    }
  })

  const _cookie = cookies.get('EWS_AUTH_KEY') ?? ''
  if (_cookie && _cookie.length > 20) {
    const payload = JSON.parse(atob(_cookie.split('.')[1]))
    const profile = payload.data

    datadogRum.setUser({
      id: profile.empid,
      name: `${profile.first_name} ${profile.last_name}`,
      email: profile.email_address
    })
  }
}

// onesignal
import OneSignalVue from 'onesignal-vue'
Vue.use(OneSignalVue)

// v-html sanitizer
import VSanitize from 'v-sanitize'
const defaultOptions = {
  allowedTags: ['span', 'p', 'b', 'em', 'u', 'i', 'a', 'br', 'div', 'details', 'summary', 'ul', 'li', 'h1', 'h2', 'h3', 'hr'],
  allowedAttributes: {
    '*': ['class', 'id', 'idref']
  }
}
Vue.use(VSanitize, defaultOptions)

// modal window plugin
import VModal from 'vue-js-modal'
Vue.use(VModal)

// alerts
import VueSimpleAlert from 'vue-simple-alert'
Vue.use(VueSimpleAlert)

// @scripps vue-components
import { Worklife, FormInput, FormButton, FormSelect, FormTextarea, FormToggle, FormWrapper, FormCheckbox } from '@/scripps-components'

Vue.component('worklife', Worklife)
Vue.component('form-wrapper', FormWrapper)
Vue.component('form-input', FormInput)
Vue.component('form-select', FormSelect)
Vue.component('form-textarea', FormTextarea)
Vue.component('form-button', FormButton)
Vue.component('form-toggle', FormToggle)
Vue.component('form-checkbox', FormCheckbox)

import { ChevronLeftIcon, ChevronRightIcon } from 'vue-feather-icons'
Vue.component('chevron-left-icon', ChevronLeftIcon)
Vue.component('chevron-right-icon', ChevronRightIcon)

Vue.filter('formatTime', function (time) {
  if (!time) {
    return
  }

  // Check correct time format and split into components
  time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time]

  if (time.length > 1) {
    // If time format correct
    time = time.slice(1) // Remove full string match value
    time[5] = +time[0] < 12 ? 'AM' : 'PM' // Set AM/PM
    time[0] = +time[0] % 12 || 12 // Adjust hours
  }
  return time.join('') // return adjusted time or original string
})

Vue.filter('formatDate', function (date, format, tz, convert, time) {
  let year = dayjs(date).$y
  let _ts = year > 1900 ? date : new Date().toString()

  tz = tz ?? 'UTC'
  format = format ?? 'sm'
  time = time ?? false

  switch (format) {
    case 'xl':
      format = time ? 'dddd, MMMM D, YYYY h:mm A' : 'dddd, MMMM D, YYYY'
      break

    case 'lg':
      format = time ? 'ddd, MMM D, YYYY h:mm A' : 'ddd, MMM D, YYYY'
      break

    case 'md':
      format = time ? 'MMM D, YYYY h:mm A' : 'MMM D, YYYY'
      break

    case 'sm':
      format = time ? 'M/D/YYYY h:mm A' : 'M/D/YYYY'
      break
  }

  if (convert) {
    if (format == 'relative') {
      return dayjs.tz(_ts, tz).local().fromNow()
    } else {
      return dayjs.tz(_ts, tz).local().format(format)
    }
  } else {
    if (format == 'relative') {
      return dayjs.tz(_ts, tz).fromNow()
    } else {
      return dayjs.tz(_ts, tz).format(format)
    }
  }
})

Vue.filter('formatSize', function (size) {
  if (!size) return
  // size = size * 1024

  if (size > 1024 * 1024 * 1024 * 1024) {
    return (size / 1024 / 1024 / 1024 / 1024).toFixed(2) + ' TB'
  } else if (size > 1024 * 1024 * 1024) {
    return (size / 1024 / 1024 / 1024).toFixed(2) + ' GB'
  } else if (size > 1024 * 1024) {
    return (size / 1024 / 1024).toFixed(2) + ' MB'
  } else if (size > 1024) {
    return (size / 1024).toFixed(2) + ' KB'
  }
  return size.toString() + ' B'
})

Vue.filter('truncate', function (text, length, clamp) {
  return text.slice(0, length) + (length < text.length ? clamp || '...' : '')
})
